import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const LANG_KEY = "weavevo_lang";
const LANG_FALLBACK_KEY = "weavevo_language";

function safeReadLang() {
  try {
    const raw =
      localStorage.getItem(LANG_KEY) ||
      localStorage.getItem(LANG_FALLBACK_KEY) ||
      "";
    const norm = String(raw || "").toLowerCase().slice(0, 2);
    return norm === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

/**
 * Discover copy + template texts (same as Wizard template details)
 */
const I18N = {
  en: {
    badge: "Discover",
    title: "Discover Weavevo",
    sub:
      "Browse the 6 collaboration templates, see how AI helps, then head to The Loom (Studio) to assemble and export your weave.",

    // Top actions
    cta_wizard: "Open Wizard",
    cta_explore: "Explore",
    cta_add_media: "Add media",
    cta_portfolio: "Open Studio",

    // Templates section
    section_templates: "Templates",
    section_templates_sub:
      "Six collaboration structures — all templates are shown with their details (same format as in the Wizard).",

    // Same keys as Wizard (details headings)
    wz_template_details_summary: "Template definition",
    wz_template_details_nature: "Concept:",
    wz_template_details_logic: "How it works:",
    wz_template_details_engagement: "Application:",
    wz_template_details_output: "Result:",

    // Template texts (copied from Wizard.js)
    tpl_SCRIPTED_label: "Storyboard",
    tpl_SCRIPTED_aka: "Precision Model",
    tpl_SCRIPTED_blurb: "A strict visual blueprint for perfect shot matching.",
    tpl_SCRIPTED_nature: "Precision Model: \"Define a sequence; every frame is a piece of the puzzle.\"",
    tpl_SCRIPTED_logic:
      "Contributors follow a strict visual blueprint (script and sketches) to ensure every shot fits perfectly into a pre-defined narrative.",
    tpl_SCRIPTED_engagement:
      "Recreating a specific camera movement or angle in your city to match a sequence filmed on the other side of the world.",
    tpl_SCRIPTED_output:
      "A tightly synchronized sequence where shots match across locations.",

    tpl_OPEN_label: "Gallery",
    tpl_OPEN_aka: "Diversity Model",
    tpl_OPEN_blurb: "An open call for many perspectives on one subject.",
    tpl_OPEN_nature: "Diversity Model: \"One subject, endless eyes. A mosaic of perspectives.\"",
    tpl_OPEN_logic:
      "An open call for a variety of perspectives on a single subject. There are no rules—just the beauty of different eyes seeing the same thing.",
    tpl_OPEN_engagement:
      "Application: Collecting hundreds of different views of a landmark or event to create a massive visual mosaic or a high-speed montage.",
    tpl_OPEN_output: "A rich mosaic built from diverse viewpoints.",

    tpl_REACTION_label: "Seed",
    tpl_REACTION_aka: "Inspiration Model",
    tpl_REACTION_blurb: "One spark, endless interpretations.",
    tpl_REACTION_nature: "Inspiration Model: \"Plant a spark and let the community’s interpretation grow.\"",
    tpl_REACTION_logic:
      'We provide a single abstract theme or a "spark," and you interpret it freely. It is designed to capture the "soul" of a subject.',
    tpl_REACTION_engagement:
      'Application: Filming what "Silence" or "History" looks like in your local environment, leading to unexpected and poetic artistic shots.',
    tpl_REACTION_output: "An expressive, poetic collection of interpretations.",

    tpl_LIVE_INSTANT_label: "Symphony",
    tpl_LIVE_INSTANT_aka: "Sensory Model",
    tpl_LIVE_INSTANT_blurb: "Each person captures a different layer.",
    tpl_LIVE_INSTANT_nature: "Sensory Model: \"Unite video, ambient sound, and macro-textures into one harmony.\"",
    tpl_LIVE_INSTANT_logic:
      "A collaborative breakdown where each person handles a specific sensory layer (video, sound, or textures).",
    tpl_LIVE_INSTANT_engagement:
      "Application: One person films the wide atmosphere, another records high-quality ambient sounds, and a third focuses on macro-details of surfaces.",
    tpl_LIVE_INSTANT_output:
      "A layered piece where visuals, sound, and textures work together.",

    tpl_TRAJECTORY_label: "Relay",
    tpl_TRAJECTORY_aka: "Continuity Model",
    tpl_TRAJECTORY_blurb: "A movement starts here, finishes elsewhere.",
    tpl_TRAJECTORY_nature: "Continuity Model: \"Build a bridge through motion. A gesture starts here and ends elsewhere.\"",
    tpl_TRAJECTORY_logic:
      "Creating a bridge through action. A movement or gesture starts in one location and is seamlessly completed in another.",
    tpl_TRAJECTORY_engagement:
      "Application: An athlete running out of the frame in New York and appearing in the next frame running into the streets of Tokyo.",
    tpl_TRAJECTORY_output:
      "A seamless action handoff that feels like one continuous take.",

    tpl_DUEL_label: "Versus",
    tpl_DUEL_aka: "Contrast Model",
    tpl_DUEL_blurb: "Split-screen comparisons that reveal meaning.",
    tpl_DUEL_nature: "The Contrast Model.",
    tpl_DUEL_logic:
      "A systematic comparison using a split-screen format to highlight the relationship between two different realities.",
    tpl_DUEL_engagement:
      'Application: Displaying "Sunrise" in the desert and "Sunrise" in a snowy city side-by-side to explore global symmetries.',
    tpl_DUEL_output:
      "A striking side-by-side that exposes contrasts and symmetries.",

    // AI section
    section_ai: "AI",
    section_ai_sub: "For now: two blocks (Image + text, then Text + image).",
    ai_block1_title: "From idea to scenario",
    ai_block1_sub:
      "Use AI to draft an inspiring scenario, then refine it with your team.",
    ai_block1_points: [
      "Turn a prompt into a coherent storyline",
      "Auto-generate scenario steps to edit",
      "Keep a consistent tone across contributors",
    ],
    ai_block2_title: "From scenario to montage",
    ai_block2_sub:
      "Use AI assistance to speed up your studio work — without losing creative control.",
    ai_block2_points: [
      "Suggest pacing, structure, and transitions",
      "Generate voice-over drafts and variations",
      "Prepare export settings for different platforms",
    ],

    // Studio section
    section_studio: "The Loom (Studio)",
    section_studio_sub:
      "Where creators assemble, review, and export the final weave.",
    studio_cta: "Go to The Loom",
    studio_cards: [
      {
        icon: "bi-collection-play",
        title: "Collect & organize",
        desc: "Bring together the best media from multiple weavers and keep everything tidy.",
      },
      {
        icon: "bi-chat-dots",
        title: "Collaborate",
        desc: "Discuss in context, react, and align on the story before final export.",
      },
      {
        icon: "bi-magic",
        title: "Montage (assisted or manual)",
        desc: "Combine human editing with AI assistance for smoother, faster production.",
      },
      {
        icon: "bi-box-arrow-up",
        title: "Export & share",
        desc: "Generate the final video and share it to your channels when it’s ready.",
      },
    ],

    footer_tip:
      "Tip: the “Discover” button is available in the top bar for quick access.",
  },

  fr: {
    badge: "Découvrir",
    title: "Découvrir Weavevo",
    sub:
      "Parcourez les 6 templates de collaboration, voyez comment l’IA aide, puis allez dans le Loom (Studio) pour assembler et exporter votre weave.",

    // Actions
    cta_wizard: "Ouvrir le Wizard",
    cta_explore: "Explorer",
    cta_add_media: "Ajouter un média",
    cta_portfolio: "Ouvrir le Studio",

    // Templates
    section_templates: "Templates",
    section_templates_sub:
      "6 structures de collaboration — sélectionnez un template pour afficher sa définition (même format que dans le Wizard).",

    // Same keys as Wizard
    wz_template_details_summary: "Définition du template",
    wz_template_details_nature: "Concept :",
    wz_template_details_logic: "Fonctionnement :",
    wz_template_details_engagement: "Application :",
    wz_template_details_output: "Résultat :",

    // Textes des templates (copiés de Wizard.js)
    tpl_SCRIPTED_label: "Storyboard",
    tpl_SCRIPTED_aka: "Modèle de précision",
    tpl_SCRIPTED_blurb:
      "Un blueprint visuel strict pour des plans parfaitement assortis.",
    tpl_SCRIPTED_nature: "Precision Model: \"Define a sequence; every frame is a piece of the puzzle.\"",
    tpl_SCRIPTED_logic:
      "Les contributeurs suivent un blueprint visuel strict (script et croquis) afin que chaque plan s’intègre parfaitement dans un récit prédéfini.",
    tpl_SCRIPTED_engagement:
      "Reproduire un mouvement de caméra ou un angle précis dans votre ville pour correspondre à une séquence filmée à l’autre bout du monde.",
    tpl_SCRIPTED_output:
      "Une séquence synchronisée, avec des plans qui se répondent d’un lieu à l’autre.",

    tpl_OPEN_label: "Galerie",
    tpl_OPEN_aka: "Modèle de diversité",
    tpl_OPEN_blurb:
      "Un appel ouvert à des points de vue multiples sur un même sujet.",
    tpl_OPEN_nature: "Diversity Model: \"One subject, endless eyes. A mosaic of perspectives.\"",
    tpl_OPEN_logic:
      "Un appel ouvert à une variété de regards sur un seul sujet. Aucune règle — juste la beauté de différentes perspectives.",
    tpl_OPEN_engagement:
      "Collecter des centaines de vues différentes d’un lieu ou d’un événement pour créer une mosaïque visuelle massive ou un montage très rapide.",
    tpl_OPEN_output:
      "Une mosaïque riche construite à partir de points de vue variés.",

    tpl_REACTION_label: "Graine",
    tpl_REACTION_aka: "Modèle d’inspiration",
    tpl_REACTION_blurb: "Une étincelle, mille interprétations.",
    tpl_REACTION_nature: "Inspiration Model: \"Plant a spark and let the community’s interpretation grow.\"",
    tpl_REACTION_logic:
      "Nous donnons un thème abstrait unique, une « étincelle », et vous l’interprétez librement. L’objectif est de capturer l’âme d’un sujet.",
    tpl_REACTION_engagement:
      "Filmer ce que « Silence » ou « Histoire » évoque dans votre environnement local, pour des plans inattendus et poétiques.",
    tpl_REACTION_output:
      "Une collection expressive et poétique d’interprétations.",

    tpl_LIVE_INSTANT_label: "Symphonie",
    tpl_LIVE_INSTANT_aka: "Modèle sensoriel",
    tpl_LIVE_INSTANT_blurb: "Chacun capture une couche différente.",
    tpl_LIVE_INSTANT_nature: "Sensory Model: \"Unite video, ambient sound, and macro-textures into one harmony.\"",
    tpl_LIVE_INSTANT_logic:
      "Une collaboration où chaque personne gère une couche sensorielle spécifique (vidéo, son ou textures).",
    tpl_LIVE_INSTANT_engagement:
      "Une personne filme l’ambiance en plan large, une autre enregistre des sons d’ambiance de haute qualité, et une troisième se concentre sur les macro‑détails des surfaces.",
    tpl_LIVE_INSTANT_output:
      "Une pièce « en couches » où images, sons et textures se complètent.",

    tpl_TRAJECTORY_label: "Relais",
    tpl_TRAJECTORY_aka: "Modèle de continuité",
    tpl_TRAJECTORY_blurb: "Un geste commence ici, se termine ailleurs.",
    tpl_TRAJECTORY_nature: "Continuity Model: \"Build a bridge through motion. A gesture starts here and ends elsewhere.\"",
    tpl_TRAJECTORY_logic:
      "Créer un pont par l’action : un mouvement ou un geste commence dans un lieu et se termine de manière fluide dans un autre.",
    tpl_TRAJECTORY_engagement:
      "Un athlète sort du cadre à New York et réapparaît dans le plan suivant en courant dans les rues de Tokyo.",
    tpl_TRAJECTORY_output:
      "Une continuité d’action qui donne l’impression d’un seul plan.",

    tpl_DUEL_label: "Versus",
    tpl_DUEL_aka: "Modèle de contraste",
    tpl_DUEL_blurb: "Comparaison en split‑screen pour révéler les liens.",
    tpl_DUEL_nature: "Le modèle de contraste.",
    tpl_DUEL_logic:
      "Une comparaison systématique en split‑screen pour mettre en lumière la relation entre deux réalités différentes.",
    tpl_DUEL_engagement:
      "Afficher « Lever de soleil » dans le désert et « Lever de soleil » dans une ville enneigée côte à côte pour explorer les symétries globales.",
    tpl_DUEL_output:
      "Un face‑à‑face percutant qui révèle contrastes et symétries.",

    // IA
    section_ai: "IA",
    section_ai_sub:
      "Pour le moment : deux blocs (Image + texte, puis Texte + image).",
    ai_block1_title: "De l’idée au scénario",
    ai_block1_sub:
      "Utilisez l’IA pour proposer un scénario inspirant, puis peaufinez‑le avec l’équipe.",
    ai_block1_points: [
      "Transformer un prompt en histoire cohérente",
      "Générer des étapes de scénario à éditer",
      "Garder un ton homogène entre contributeurs",
    ],
    ai_block2_title: "Du scénario au montage",
    ai_block2_sub:
      "L’IA accélère le travail studio — sans vous enlever le contrôle créatif.",
    ai_block2_points: [
      "Suggérer rythme, structure et transitions",
      "Proposer des variantes de voix‑off",
      "Préparer des exports adaptés aux plateformes",
    ],

    // Loom / Studio
    section_studio: "Le Loom (Studio)",
    section_studio_sub:
      "Là où les créateurs assemblent, valident et exportent le weave final.",
    studio_cta: "Aller au Loom",
    studio_cards: [
      {
        icon: "bi-collection-play",
        title: "Collecter & organiser",
        desc: "Rassembler les meilleurs médias de plusieurs weavers et garder tout bien structuré.",
      },
      {
        icon: "bi-chat-dots",
        title: "Collaborer",
        desc: "Discuter, réagir et s’aligner sur l’histoire avant l’export final.",
      },
      {
        icon: "bi-magic",
        title: "Montage (assisté ou manuel)",
        desc: "Mixer montage humain et assistance IA pour produire plus vite et plus propre.",
      },
      {
        icon: "bi-box-arrow-up",
        title: "Exporter & partager",
        desc: "Générer la vidéo finale et la partager sur vos canaux quand elle est prête.",
      },
    ],

    footer_tip:
      "Astuce : le bouton “Discover” est disponible dans la barre du haut pour y accéder rapidement.",
  },
};

/** ***********************************
 * Templates (same keys as Wizard)
 ************************************ */
const TEMPLATE_OPTIONS = [
  { key: "SCRIPTED", icon: "bi-film" },
  { key: "OPEN", icon: "bi-grid-3x3-gap" },
  { key: "REACTION", icon: "bi-lightning-charge" },
  { key: "LIVE_INSTANT", icon: "bi-broadcast" },
  { key: "TRAJECTORY", icon: "bi-geo-alt" },
  { key: "DUEL", icon: "bi-columns-gap" },
];

const DEFAULT_TEMPLATE_KEY = "SCRIPTED";


// Template visuals (served from frontend/public/landing)
const TEMPLATE_VISUALS = {
  SCRIPTED: "/landing/tmplt_storyboard.png",
  OPEN: "/landing/tmplt_gallery.png",
  REACTION: "/landing/tmplt_Seed.png",
  LIVE_INSTANT: "/landing/tmplt_Symphony.png",
  TRAJECTORY: "/landing/tmplt_Relay.png",
  DUEL: "/landing/templVersus.png",
};

const templateVisualSrc = (key) => {
  const k = (key || DEFAULT_TEMPLATE_KEY).toString().trim().toUpperCase();
  return TEMPLATE_VISUALS[k] || "";
};


const templateTextKey = (key, suffix) => `tpl_${key}_${suffix}`;

const localizeTemplate = (tpl, t) => {
  const tx = typeof t === "function" ? t : (k) => k;
  const raw = (tpl?.key || "").toString().trim().toUpperCase();
  const key = TEMPLATE_OPTIONS.some((x) => x.key === raw) ? raw : DEFAULT_TEMPLATE_KEY;

  const pick = (field) => tx(templateTextKey(key, field));
  return {
    ...tpl,
    key,
    label: pick("label"),
    aka: pick("aka"),
    blurb: pick("blurb"),
    nature: pick("nature"),
    logic: pick("logic"),
    engagement: pick("engagement"),
    output: pick("output"),
  };
};


/** ***********************************
 * Inline CSS (Wizard-like + Discover)
 ************************************ */
const DiscoverStyles = React.memo(function DiscoverStyles() {
  return (
    <style>{`
      .btn-weave{
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: .5rem;
        box-shadow: 0 6px 18px rgba(0,0,0,.06);
      }
      .btn-weave:disabled{ box-shadow: none; }

      /* ✅ Force your Bootstrap-like PRIMARY palette on ALL buttons in this page (except links) */
      .wizard-scope .btn:not(.btn-link){
        --bs-btn-color: #fff;
        --bs-btn-bg: #0d6efd;
        --bs-btn-border-color: #0d6efd;
        --bs-btn-hover-color: #fff;
        --bs-btn-hover-bg: #0b5ed7;
        --bs-btn-hover-border-color: #0a58ca;
        --bs-btn-focus-shadow-rgb: 49, 132, 253;
        --bs-btn-active-color: #fff;
        --bs-btn-active-bg: #0a58ca;
        --bs-btn-active-border-color: #0a53be;
        --bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, .125);
        --bs-btn-disabled-color: #fff;
        --bs-btn-disabled-bg: #0d6efd;
        --bs-btn-disabled-border-color: #0d6efd;

        --bs-btn-padding-y: 0.25rem;
        --bs-btn-padding-x: 0.5rem;
        --bs-btn-font-size: 0.875rem;
        --bs-btn-border-radius: var(--bs-border-radius-sm);

        color: var(--bs-btn-color) !important;
        background-color: var(--bs-btn-bg) !important;
        border-color: var(--bs-btn-border-color) !important;
      }
      .wizard-scope .btn:not(.btn-link):hover{
        color: var(--bs-btn-hover-color) !important;
        background-color: var(--bs-btn-hover-bg) !important;
        border-color: var(--bs-btn-hover-border-color) !important;
      }
      .wizard-scope .btn:not(.btn-link):focus,
      .wizard-scope .btn:not(.btn-link):focus-visible{
        box-shadow: 0 0 0 .25rem rgba(var(--bs-btn-focus-shadow-rgb), .5) !important;
      }
      .wizard-scope .btn:not(.btn-link):active,
      .wizard-scope .btn:not(.btn-link).active{
        color: var(--bs-btn-active-color) !important;
        background-color: var(--bs-btn-active-bg) !important;
        border-color: var(--bs-btn-active-border-color) !important;
        box-shadow: var(--bs-btn-active-shadow) !important;
      }
      .wizard-scope .btn:not(.btn-link):disabled,
      .wizard-scope .btn:not(.btn-link).disabled{
        color: var(--bs-btn-disabled-color) !important;
        background-color: var(--bs-btn-disabled-bg) !important;
        border-color: var(--bs-btn-disabled-border-color) !important;
        opacity: .65 !important;
      }

      /* ✅ HERO subtitle + actions (no horizontal scrollbar) */
      .dv-hero-inline{
        display:flex;
        align-items:center;
        gap: 14px;
        flex-wrap: wrap;
        overflow-x: hidden;
      }
      .dv-hero-sub{
        color: rgba(0,0,0,.55);
        min-width: 320px;
        flex: 1 1 520px;
      }
      .dv-hero-actions{
        display:flex;
        align-items:center;
        gap: 8px;
        flex-wrap: wrap;
        flex: 0 0 auto;
      }
      /* ✅ Keep single-line hero only on very wide screens (22"+).
         On typical 14" laptops (1366px wide), allow wrapping so nothing gets cut. */
      @media (min-width: 1600px){
        .dv-hero-inline{ flex-wrap: nowrap; }
        .dv-hero-sub{
          white-space: nowrap;
          min-width: 0;
          flex: 1 1 auto;
        }
        .dv-hero-actions{ flex-wrap: nowrap; }
      }

      /* ✅ Compact & safe layout for 14" class screens (keeps 22" unchanged) */
      @media (max-width: 1399.98px){
        .dv-hero-sub{ white-space: normal; font-size: .95rem; }
        .dv-template-visual img{ height: clamp(160px, 14vw, 240px); }
        .dv-template-title{ font-size: .92rem; }
        .dv-template-aka{ font-size: .76rem; }
        .dv-template-blurb{ font-size: .80rem; }
        .dv-details{ padding: 9px 10px; }
        .dv-line{ font-size: .80rem; }
        .dv-k{ min-width: 86px; }
      }

      @media (max-height: 820px){
        .dv-illust{ min-height: 190px; }
      }


      /* Template pill (same as Wizard) */
      .wv-template-pill{
        width:100%;
        border:1px solid rgba(0,0,0,.08);
        background:#fff;
        border-radius:14px;
        padding:.55rem .6rem;
        display:flex;
        align-items:center;
        gap:.55rem;
        text-align:left;
        cursor:pointer;
        transition:transform .12s ease, border-color .12s ease, background .12s ease;
        line-height:1.1;
      }
      .wv-template-pill:hover{
        border-color: rgba(13,110,253,.35);
        background: rgba(13,110,253,.03);
        transform: translateY(-1px);
      }
      .wv-template-pill.is-active{
        border-color: rgba(13,110,253,.65);
        background: rgba(13,110,253,.06);
      }
      .wv-template-pill i{
        font-size:16px;
        opacity:.85;
        flex:0 0 auto;
      }
      .wv-template-pill.is-active i{ opacity:1; }
      .wv-template-text{ display:flex; flex-direction:column; min-width:0; }
      .wv-template-label{
        font-size:.84rem;
        font-weight:700;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }
      .wv-template-aka{
        font-size:.72rem;
        opacity:.65;
        white-space:nowrap;
        overflow:hidden;
        text-overflow:ellipsis;
      }

      .dv-ico{
        width: 42px; height: 42px;
        border-radius: 14px;
        background: rgba(13,110,253,.08);
        display:flex; align-items:center; justify-content:center;
        flex: 0 0 auto;
        border: 1px solid rgba(13,110,253,.14);
      }
      .dv-ico i{ font-size: 18px; color: #0d6efd; }

      .dv-soft{
        background: rgba(0,0,0,.015);
        border: 1px solid rgba(0,0,0,.06);
        border-radius: 18px;
      }

      /* Templates grid (fit all templates without horizontal scroll/click) */
      .dv-template-grid .dv-soft{ padding: 12px !important; }
      .dv-template-title{
        font-weight: 600;
        font-size: .96rem;
        line-height: 1.15;
      }
      .dv-template-aka{
        font-size: .78rem;
        opacity: .65;
        margin-top: 2px;
        line-height: 1.1;
      }
            .dv-template-visual{
        border-radius: 16px;
        overflow: hidden;
        border: 1px solid rgba(0,0,0,.06);
        background: #fff;
      }
      .dv-template-visual img{
        width: 100%;
        height: clamp(190px, 18vw, 300px);
        object-fit: contain;
        display: block;
      }

      /* Click-to-zoom for template visuals */
      .wv-zoom-btn{
        width: 100%;
        border: 0;
        padding: 0;
        background: transparent;
        display: block;
        cursor: zoom-in;
      }
      .wv-zoom-btn:focus-visible{
        outline: 3px solid rgba(13,110,253,.35);
        outline-offset: 3px;
        border-radius: 16px;
      }

      /* Lightbox */
      .wv-lightbox-backdrop{
        position: fixed;
        inset: 0;
        z-index: 2000;
        background: rgba(0,0,0,.72);

        /* ✅ Robust centering on small/short viewports (14") */
        display: flex;
        align-items: center;
        justify-content: center;

        padding: 18px;
        box-sizing: border-box;
        overflow: auto;
      }
      .wv-lightbox{
        position: relative;
        margin: auto;
        width: min(1000px, calc(100vw - 36px));
        height: min(860px, calc(100vh - 36px));
        border-radius: 18px;
        overflow: hidden;
        background: #fff;
        box-shadow: 0 18px 60px rgba(0,0,0,.55);
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .wv-lightbox img{
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;
        object-position: 50% 50%;
        display: block;
        background: #fff;
      }
      .wv-lightbox-close{
        position: absolute;
        top: 10px;
        right: 10px;
        width: 40px;
        height: 40px;
        border-radius: 999px;
        border: 1px solid rgba(0,0,0,.10);
        background: rgba(255,255,255,.85);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .wv-lightbox-close:hover{
        background: rgba(255,255,255,.95);
      }


.dv-template-blurb{
        font-size: .82rem;
        color: rgba(0,0,0,.62);
        margin-top: 6px;
      }
      .dv-details{
        margin-top: 10px;
        border-radius: 16px;
        border: 1px solid rgba(0,0,0,.05);
        background: rgba(0,0,0,.015);
        padding: 10px 12px;
      }
      .dv-line{
        display:flex;
        gap: 8px;
        align-items:flex-start;
        margin: 6px 0;
        font-size: .82rem;
        color: rgba(0,0,0,.75);
      }
      .dv-k{
        font-weight: 600;
        min-width: 92px;
        color: rgba(0,0,0,.72);
        line-height: 1.15;
      }
      .dv-v{
        flex: 1 1 auto;
        min-width: 0;
        line-height: 1.15;
      }
      .dv-clamp-2{
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      /* AI illustrations */
      .dv-illust{
        min-height: 220px;
        border-radius: 18px;
        background:
          radial-gradient(1200px 240px at 10% 10%, rgba(13,110,253,.30), transparent 50%),
          radial-gradient(800px 240px at 90% 60%, rgba(99,102,241,.22), transparent 45%),
          linear-gradient(135deg, rgba(17,24,39,.98), rgba(17,24,39,.92));
        border: 1px solid rgba(255,255,255,.10);
        color: rgba(255,255,255,.92);
        display:flex;
        align-items:flex-end;
        padding: 16px;
        position: relative;
        overflow: hidden;
      }
      .dv-illust .dv-illust-badge{
        position: absolute;
        top: 14px;
        left: 14px;
        display: inline-flex;
        align-items:center;
        gap: 8px;
        padding: 6px 10px;
        border-radius: 999px;
        background: rgba(255,255,255,.10);
        border: 1px solid rgba(255,255,255,.16);
        backdrop-filter: blur(6px);
      }
      .dv-illust .dv-illust-badge i{ opacity: .95; }
      .dv-illust .dv-illust-footer{
        font-weight: 800;
        letter-spacing: -0.01em;
        line-height: 1.05;
      }
    `}</style>
  );
});

export default function DiscoverWeavevo() {
  const { i18n } = useTranslation();

  const lang = useMemo(() => {
    const l = safeReadLang();
    try {
      const current = String(i18n?.language || "").toLowerCase().slice(0, 2);
      if (i18n?.changeLanguage && current !== l) {
        i18n.changeLanguage(l).catch(() => {});
      }
    } catch {}
    return l;
  }, [i18n]);

  const dict = I18N[lang] || I18N.en;
  const t = useCallback(
    (key) => {
      const out = dict?.[key] ?? I18N.en?.[key] ?? key;
      return out;
    },
    [dict]
  );

  // Templates: show ALL cards with details (no click, no scroll)
  const templates = useMemo(
    () => TEMPLATE_OPTIONS.map((tpl) => localizeTemplate(tpl, t)),
    [t]
  );


  // Lightbox (click template visual to open full size)
  const [lightbox, setLightbox] = useState({ src: "", alt: "" });

  const closeLightbox = useCallback(() => {
    setLightbox({ src: "", alt: "" });
  }, []);

  const openLightbox = useCallback((src, alt) => {
    if (!src) return;
    setLightbox({ src, alt: alt || "" });
  }, []);

  useEffect(() => {
    if (!lightbox.src) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.src, closeLightbox]);


  return (
    <div className="container-fluid py-3 wizard-scope" style={{ overflowX: "hidden" }}>
      {/* wider container but still centered */}
      <div className="container" style={{ maxWidth: 1440 }}>
        {/* HERO */}
        <div>
          <h1
            className="mt-3 mb-2"
            style={{ fontWeight: 500, letterSpacing: "-0.02em" }}
          >
            {t("title")}
          </h1>

          {/* INLINE subtitle + actions (single line, no wrap) */}
          <div className="dv-hero-inline">
            <span className="dv-hero-sub">{t("sub")}</span>

            <div className="dv-hero-actions">
              <Link to="/wizard" className="btn btn-primary btn-weave">
                <i className="bi bi-magic" />
                {t("cta_wizard")}
              </Link>
              <Link to="/explore" className="btn btn-outline-secondary btn-weave">
                <i className="bi bi-compass" />
                {t("cta_explore")}
              </Link>
              <Link to="/create-media" className="btn btn-outline-secondary btn-weave">
                <i className="bi bi-upload" />
                {t("cta_add_media")}
              </Link>
              <Link to="/portfolio" className="btn btn-outline-secondary btn-weave">
                <i className="bi bi-layout-text-window-reverse" />
                {t("cta_portfolio")}
              </Link>
            </div>
          </div>
        </div>

        <hr className="my-4" style={{ opacity: 0.12 }} />

        {/* SECTION — Templates */}
        <section>
          <div className="d-flex align-items-end justify-content-between flex-wrap gap-2">
            <div>
              <h2 className="m-0" style={{ fontWeight: 500 }}>
                {t("section_templates")}
              </h2>
              <div className="text-muted" style={{ maxWidth: 980 }}>
                {t("section_templates_sub")}
              </div>
            </div>
          </div>

          {/* All templates with details (no click, no horizontal scroll) */}
          <div className="row g-3 mt-3 dv-template-grid">
            {templates.map((view) => (
              <div className="col-12 col-md-6 col-xl-4" key={view.key}>
                <div className="dv-soft p-3 h-100">
                  <div className="d-flex align-items-start gap-2">
                    <div className="dv-ico" aria-hidden="true">
                      <i className={`bi ${view.icon}`} />
                    </div>
                    <div className="min-w-0 flex-grow-1">
                      <div className="dv-template-title">{view.label}</div>
                      <div className="dv-template-aka">{view.aka}</div>
                      <div className="dv-template-visual mt-2">
                        <button
                          type="button"
                          className="wv-zoom-btn"
                          onClick={() =>
                            openLightbox(
                              templateVisualSrc(view.key),
                              `${view.label} template visual`
                            )
                          }
                          aria-label={`Open ${view.label} template visual`}
                        >
                          <img
                            src={templateVisualSrc(view.key)}
                            alt={`${view.label} template visual`}
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </button>
                      </div>
                      <div className="dv-template-blurb dv-clamp-2">
                        {view.blurb}
                      </div>
                    </div>
                  </div>

                  <div className="dv-details mt-2">
                    <div className="dv-line">
                      <div className="dv-k">{t("wz_template_details_nature")}</div>
                      <div className="dv-v dv-clamp-2">{view.nature}</div>
                    </div>
                    <div className="dv-line">
                      <div className="dv-k">{t("wz_template_details_logic")}</div>
                      <div className="dv-v dv-clamp-2">{view.logic}</div>
                    </div>
                    <div className="dv-line">
                      <div className="dv-k">
                        {t("wz_template_details_engagement")}
                      </div>
                      <div className="dv-v dv-clamp-2">{view.engagement}</div>
                    </div>
                    <div className="dv-line">
                      <div className="dv-k">{t("wz_template_details_output")}</div>
                      <div className="dv-v dv-clamp-2">{view.output}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-5">
          <div className="d-flex align-items-end justify-content-between flex-wrap gap-2">
            <div>
              <h2 className="m-0" style={{ fontWeight: 500 }}>
                {t("section_ai")}
              </h2>
              <div className="text-muted" style={{ maxWidth: 980 }}>
                {t("section_ai_sub")}
              </div>
            </div>
          </div>

          <div className="row g-3 mt-2">
            {/* Image + Text */}
            <div className="col-12">
              <div className="card border-0 rounded-4 shadow-sm overflow-hidden">
                <div className="row g-0 align-items-stretch">
                  <div className="col-12 col-lg-5">
                    <div className="dv-illust h-100">
                      <div className="dv-illust-badge">
                        <i className="bi bi-stars" />
                        <span className="small fw-semibold">AI</span>
                      </div>
                      <div className="dv-illust-footer">
                        Prompt → Scenario
                        <br />
                        Steps → Story
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-7">
                    <div className="p-3 p-md-4">
                      <h5 className="mb-1" style={{ fontWeight: 500 }}>
                        {t("ai_block1_title")}
                      </h5>
                      <div className="text-muted">{t("ai_block1_sub")}</div>

                      <div className="mt-3 dv-soft p-3">
                        <ul className="m-0" style={{ paddingLeft: 18 }}>
                          {(t("ai_block1_points") || []).map((x, i) => (
                            <li key={i} className="mb-1">
                              {x}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-3 d-flex flex-wrap gap-2">
                        <Link to="/wizard" className="btn btn-primary btn-weave">
                          <i className="bi bi-magic" />
                          {t("cta_wizard")}
                        </Link>
                        <Link
                          to="/explore"
                          className="btn btn-outline-secondary btn-weave"
                        >
                          <i className="bi bi-compass" />
                          {t("cta_explore")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Text + Image */}
            <div className="col-12">
              <div className="card border-0 rounded-4 shadow-sm overflow-hidden">
                <div className="row g-0 align-items-stretch flex-lg-row-reverse">
                  <div className="col-12 col-lg-5">
                    <div className="dv-illust h-100">
                      <div className="dv-illust-badge">
                        <i className="bi bi-robot" />
                        <span className="small fw-semibold">Studio</span>
                      </div>
                      <div className="dv-illust-footer">
                        Scenario → Montage
                        <br />
                        Export → Share
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-7">
                    <div className="p-3 p-md-4">
                      <h5 className="mb-1" style={{ fontWeight: 500 }}>
                        {t("ai_block2_title")}
                      </h5>
                      <div className="text-muted">{t("ai_block2_sub")}</div>

                      <div className="mt-3 dv-soft p-3">
                        <ul className="m-0" style={{ paddingLeft: 18 }}>
                          {(t("ai_block2_points") || []).map((x, i) => (
                            <li key={i} className="mb-1">
                              {x}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-3 d-flex flex-wrap gap-2">
                        <Link
                          to="/portfolio"
                          className="btn btn-primary btn-weave"
                        >
                          <i className="bi bi-layout-text-window-reverse" />
                          {t("cta_portfolio")}
                        </Link>
                        <Link
                          to="/create-media"
                          className="btn btn-outline-secondary btn-weave"
                        >
                          <i className="bi bi-upload" />
                          {t("cta_add_media")}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION — The Loom / Studio */}
        <section className="mt-5">
          <div className="card border-0 rounded-4 shadow-sm">
            <div className="card-body">
              <div className="d-flex align-items-end justify-content-between flex-wrap gap-2">
                <div>
                  <h2 className="m-0" style={{ fontWeight: 500 }}>
                    {t("section_studio")}
                  </h2>
                  <div className="text-muted" style={{ maxWidth: 980 }}>
                    {t("section_studio_sub")}
                  </div>
                </div>

                <Link to="/portfolio" className="btn btn-primary btn-weave">
                  <i className="bi bi-layout-text-window-reverse" />
                  {t("studio_cta")}
                </Link>
              </div>

              <div className="row g-3 mt-2">
                {(t("studio_cards") || []).map((c, idx) => (
                  <div className="col-12 col-md-6 col-lg-3" key={idx}>
                    <div className="dv-soft p-3 h-100">
                      <div className="d-flex align-items-center gap-2">
                        <div className="dv-ico" aria-hidden="true">
                          <i className={`bi ${c.icon}`} />
                        </div>
                        <div className="fw-bold">{c.title}</div>
                      </div>
                      <div className="text-muted mt-2">{c.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </section>

        <DiscoverStyles />

        {lightbox.src ? (
          <div
            className="wv-lightbox-backdrop"
            role="dialog"
            aria-modal="true"
            onClick={closeLightbox}
          >
            <div className="wv-lightbox" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="wv-lightbox-close"
                onClick={closeLightbox}
                aria-label="Close"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
              <img src={lightbox.src} alt={lightbox.alt || ""} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
